import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { SkyDropdown } from "@skypointcloud/skypoint-ui";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ChevronDown, Check } from "lucide-react";
import { ROUTES } from "../../../../components/shared/constant";
import { useMsalAuth } from "../../../../hooks/useMsalAuth";
import { useProductOptions } from "../../../../hooks/useProductOptions";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { isWorkforceContext } from "../../../../routes/workforcePaths";
import { isEmployeeContext, isEmployeeUserViewPath } from "../../../../routes/employeePaths";
import { PRODUCT_OPTIONS, type ProductValue } from "./productOptions";

export interface ProductSwitcherProps {
  /** "header": pill trigger + popover on the right of the desktop header. "menu": bordered row inside ProfileMenu. */
  variant?: "header" | "menu";
  /** Called after a selection navigates (e.g. to close the profile popover). */
  onNavigate?: () => void;
}

export function ProductSwitcher({ variant = "header", onNavigate }: ProductSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getQueryString } = useMsalAuth();
  const { enabledOptions } = useProductOptions();
  const [menuOpen, setMenuOpen] = useState(false);
  // Same rule as the portal-view switch: admins and managers have the agent
  // console. Everyone else's Service Desk is the customer portal (a plain
  // employee sees this switcher inside skyEmployee).
  const { data: rbacPermissions } = useGetMyPermissionsQuery();
  const hasAgentConsole =
    rbacPermissions?.isAdmin === true ||
    rbacPermissions?.isB2CAdmin === true ||
    rbacPermissions?.isManager === true;

  // A switcher with a single destination is just noise — Service Desk is always
  // enabled, so this hides the control until a second product is available.
  if (enabledOptions.length < 2) return null;
  // skyEmployee's employee-facing view is the user portal: no product switching
  // there (admins leave it through Portal View → Admin).
  if (isEmployeeUserViewPath(location.pathname)) return null;

  // Module-neutral routes (/account) keep the module you came from, so opening
  // "View profile" from Workforce no longer flips the pill to Service Desk.
  // …but only into a product that's still on: a stale from=employee link after
  // Employee was switched off reads Service Desk, like the sidebar.
  const employeeOn = enabledOptions.some((o) => o.value === "employee");
  const current: ProductValue =
    isEmployeeContext(location.pathname, location.search) && employeeOn
      ? "employee"
      : isWorkforceContext(location.pathname, location.search)
        ? "workforce"
        : "service-desk";
  const currentOption = PRODUCT_OPTIONS.find((o) => o.value === current) ?? PRODUCT_OPTIONS[0];
  const CurrentIcon = currentOption.Icon;

  const goTo = (value: ProductValue) => {
    onNavigate?.();
    const qs = getQueryString();
    const target =
      value === "service-desk" && !hasAgentConsole
        ? ROUTES.CUSTOMER_PORTALS
        : (PRODUCT_OPTIONS.find((o) => o.value === value)?.route ?? ROUTES.HOME);
    navigate(target + qs);
  };

  const handleSelect = (values: string[]) => {
    const value = values[0] as ProductValue;
    if (value === current) return;
    goTo(value);
  };

  const handlePillSelect = (value: ProductValue) => {
    setMenuOpen(false);
    if (value === current) return;
    goTo(value);
  };

  if (variant === "menu") {
    return (
      <div className="px-4 py-3 border-b border-gray-200">
        <SkyDropdown
          label="Product"
          size="small"
          options={enabledOptions}
          selectedOptions={[current]}
          onOptionSelect={handleSelect}
        />
      </div>
    );
  }

  return (
    <PopoverPrimitive.Root
      open={menuOpen}
      onOpenChange={setMenuOpen}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Switch product"
          className="flex items-center gap-2 h-9 px-2.5 rounded-full bg-white hover:bg-gray-100 border border-white/20 transition-colors"
        >
          <span
            className={`flex items-center justify-center w-6 h-6 rounded-md ${currentOption.iconClass}`}
          >
            <CurrentIcon className="w-3.5 h-3.5" />
          </span>
          <span className="text-sm font-semibold text-gray-900">{currentOption.text}</span>
          <ChevronDown
            size={16}
            className="text-gray-500"
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={8}
          className="p-1.5 rounded-xl shadow-xl border border-white/40 dark:border-white/10 min-w-56 bg-white/75 dark:bg-gray-900/75 backdrop-blur-xl backdrop-saturate-150 z-[1050]"
        >
          {enabledOptions.map((option) => {
            const OptionIcon = option.Icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handlePillSelect(option.value)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-left hover:bg-white/70 dark:hover:bg-white/10 transition-colors"
              >
                <span
                  className={`flex items-center justify-center w-6 h-6 rounded-md ${option.iconClass}`}
                >
                  <OptionIcon className="w-3.5 h-3.5" />
                </span>
                <span className="flex-1 text-gray-900 dark:text-gray-100">{option.text}</span>
                {option.value === current && (
                  <Check
                    size={16}
                    className="text-amber-600 dark:text-amber-400"
                  />
                )}
              </button>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
